import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { syncSubscription, createPortalSession } from '../../services/stripeService';
import { getSubscriptionDisplayName, mapPlanIdToSubscriptionType } from '../../utils/accountHelpers';
import { isRawStripeSubscription, sanitizeSubscription } from '../../utils/subscriptionHelpers';
import { logger } from '../../utils/logger';

export default function SubscriptionStatus() {
  const { t, i18n } = useTranslation();
  const { user, reloadUser } = useAuth();
  const [syncing, setSyncing] = React.useState(false);
  const [message, setMessage] = React.useState({ type: '', text: '' });

  // CRITICAL: Validate and sanitize subscription data to prevent rendering raw Stripe objects
  let subscription = user?.subscription || null;
  
  // Detect and handle raw Stripe subscription objects
  React.useEffect(() => {
    if (subscription && isRawStripeSubscription(subscription) && user?.uid && !syncing) {
      logger.error('❌ CRITICAL: Raw Stripe subscription object detected in SubscriptionStatus!');
      
      // Trigger sync to fix corrupted data
      setSyncing(true);
      syncSubscription(user.uid).then((result) => {
        if (result.success) {
          logger.log('Subscription synced successfully, reloading user data...');
          reloadUser();
        } else {
          logger.error('Failed to sync subscription:', result.error);
          setMessage({ 
            type: 'error', 
            text: i18n.language === 'es' 
              ? 'Error al sincronizar suscripción. Por favor, recarga la página.' 
              : 'Error syncing subscription. Please refresh the page.' 
          });
        }
      }).catch((error) => {
        logger.error('Error syncing subscription:', error);
        setMessage({ 
          type: 'error', 
          text: i18n.language === 'es' 
            ? 'Error al sincronizar suscripción. Por favor, recarga la página.' 
            : 'Error syncing subscription. Please refresh the page.' 
        });
      }).finally(() => {
        setSyncing(false);
      });
    }
  }, [subscription, user?.uid, syncing, i18n.language, reloadUser]);
  
  // Sanitize subscription data - ensure all nested values are primitives, not objects
  subscription = sanitizeSubscription(subscription);
  
  // If subscription is a raw Stripe object, show error message
  if (subscription === null && user?.subscription && isRawStripeSubscription(user.subscription)) {
    return (
      <div>
        <h2 className="text-xl font-semibold text-gray-300 mb-6">
          {i18n.language === 'es' ? 'Estado de Suscripción' : 'Subscription Status'}
        </h2>
        <div className="bg-red-900/50 rounded-lg p-6 border border-red-500 text-center">
          <p className="text-red-300 mb-4">
            {i18n.language === 'es' 
              ? 'Error: Datos de suscripción corruptos detectados. Sincronizando...' 
              : 'Error: Corrupted subscription data detected. Syncing...'}
          </p>
          {syncing && (
            <p className="text-gray-400 text-sm">
              {i18n.language === 'es' ? 'Por favor espera...' : 'Please wait...'}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Format currency
  const formatCurrency = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount || 0);
  };

  // Helper function to determine actual subscription status.
  // For recurring Stripe subscriptions, prefer Stripe lifecycle status over local date math.
  const getActualStatus = () => {
    if (!subscription) return 'expired';
    
    const status = subscription.status || 'expired';
    const planId = subscription.planId;
    const isFreeTrial = planId === 'free_trial';
    const cancelAtPeriodEnd = subscription.cancelAtPeriodEnd || false;

    // Explicit Stripe terminal states
    if (status === 'expired' || status === 'past_due' || status === 'unpaid') {
      return 'expired';
    }

    // Canceled but still in paid-through period
    if (cancelAtPeriodEnd || status === 'canceled' || status === 'cancelled') {
      return 'cancelled';
    }

    // Keep free trial guardrails date-based
    if (isFreeTrial) {
      const expirationDate = subscription.trialEndsAt || subscription.currentPeriodEnd;
      if (expirationDate) {
        const expirationDateObj = new Date(expirationDate);
        if (expirationDateObj < new Date()) {
          return 'expired';
        }
      } else if (subscription.sessionsRemaining !== undefined && subscription.sessionsRemaining <= 0) {
        return 'expired';
      }
      return 'trialing';
    }

    if (status === 'trialing') return 'trialing';
    if (status === 'active' || status === 'incomplete') return 'active';

    return status || 'expired';
  };

  // Get subscription plan badge - always shows plan name, not status
  const getStatusBadge = () => {
    if (!subscription) {
      return {
        label: i18n.language === 'es' ? 'Sin Suscripción' : 'No Subscription',
        color: 'bg-gray-700 text-gray-300',
        border: 'border-gray-600'
      };
    }

    const planId = subscription.planId || 'expired';
    
    // Always show plan name, regardless of status
    if (planId === 'expired' || planId === 'none') {
      return {
        label: i18n.language === 'es' ? 'Sin Suscripción' : 'No Subscription',
        color: 'bg-gray-700 text-gray-300',
        border: 'border-gray-600'
      };
    }

    // Map planId to subscription type for display
    const subscriptionType = mapPlanIdToSubscriptionType(planId);
    const planName = getSubscriptionDisplayName(subscriptionType);
    
    // Determine color based on actual status (checking dates)
    const actualStatus = getActualStatus();
    let color = 'bg-gray-700 text-gray-300';
    let border = 'border-gray-600';
    
    if (actualStatus === 'active') {
      color = 'bg-green-900/50 text-green-300';
      border = 'border-green-600';
    } else if (actualStatus === 'trialing') {
      color = 'bg-blue-900/50 text-blue-300';
      border = 'border-blue-600';
    } else if (actualStatus === 'canceled' || actualStatus === 'cancelled') {
      color = 'bg-yellow-900/50 text-yellow-300';
      border = 'border-yellow-600';
    } else if (actualStatus === 'expired') {
      color = 'bg-gray-700 text-gray-300';
      border = 'border-gray-600';
    }
    
    return {
      label: planName,
      color: color,
      border: border
    };
  };

  // Handle manage subscription button click
  // Tries API-based portal session first (better UX), falls back to static login link
  const handleManageSubscription = async () => {
    if (!user?.uid) {
      setMessage({ 
        type: 'error', 
        text: i18n.language === 'es' 
          ? 'Usuario no autenticado' 
          : 'User not authenticated' 
      });
      return;
    }

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Debug-Session-Id': '60e2e9',
      },
      body: JSON.stringify({
        sessionId: '60e2e9',
        runId: 'initial',
        hypothesisId: 'H1',
        location: 'src/components/account/SubscriptionStatus.jsx:211',
        message: 'handleManageSubscription invoked',
        data: {
          hasUser: !!user,
          userId: user?.uid,
          hasSubscription: !!subscription,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion

    setSyncing(true);
    setMessage({ 
      type: 'info', 
      text: i18n.language === 'es' 
        ? 'Abriendo portal de gestión...' 
        : 'Opening subscription management...' 
    });

    // Fallback function: Use static Stripe Customer Portal login link
    const redirectToStaticLoginLink = () => {
      // Get portal login link from environment variable (fallback to test link if not set)
      const portalLoginLink = import.meta.env.VITE_STRIPE_CUSTOMER_PORTAL_LINK || 
        'https://billing.stripe.com/p/login/test_fZufZj55Hc4nfGt6pJaVa00';
      
      // Add prefilled email if user email is available (improves UX)
      let portalUrl = portalLoginLink;
      if (user?.email) {
        const encodedEmail = encodeURIComponent(user.email);
        const separator = portalLoginLink.includes('?') ? '&' : '?';
        portalUrl = `${portalLoginLink}${separator}prefilled_email=${encodedEmail}`;
      }
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Debug-Session-Id': '60e2e9',
        },
        body: JSON.stringify({
          sessionId: '60e2e9',
          runId: 'initial',
          hypothesisId: 'H2',
          location: 'src/components/account/SubscriptionStatus.jsx:231',
          message: 'redirectToStaticLoginLink computed URL',
          data: {
            hasPortalEnv: !!import.meta.env.VITE_STRIPE_CUSTOMER_PORTAL_LINK,
            portalHost: (() => {
              try {
                const u = new URL(portalUrl);
                return u.hostname;
              } catch {
                return 'invalid-url';
              }
            })(),
          },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion

      // Open portal in same window
      window.location.href = portalUrl;
    };

    try {
      // Try API-based portal session first (provides direct authenticated access)
      const result = await createPortalSession(user.uid);
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Debug-Session-Id': '60e2e9',
        },
        body: JSON.stringify({
          sessionId: '60e2e9',
          runId: 'initial',
          hypothesisId: 'H1',
          location: 'src/components/account/SubscriptionStatus.jsx:249',
          message: 'createPortalSession result',
          data: {
            success: !!result?.success,
            hasUrl: !!result?.url,
            error: result?.error || null,
          },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion
      
      if (result.success && result.url) {
        // Verify URL is a session URL (not a login page)
        if (result.url.includes('/p/session/')) {
          // Good - it's a proper session URL
          window.location.href = result.url;
          return;
        } else if (result.url.includes('/p/login/')) {
          // Got login URL instead - fall back to static login link
          logger.warn('API returned login URL, using static login link instead');
          redirectToStaticLoginLink();
        } else {
          // Unknown URL format - try it anyway
          logger.warn('Unexpected portal URL format, redirecting anyway');
          window.location.href = result.url;
        }
      } else {
        // API failed - fall back to static login link
        logger.warn('Portal session creation failed, using static login link', {
          error: result.error,
          // Log additional context for debugging
          userId: user?.uid,
          hasStripeCustomerId: !!subscription?.stripeCustomerId
        });
        
        // Show user-friendly error message before redirecting
        setMessage({
          type: 'error',
          text: i18n.language === 'es'
            ? 'No se pudo conectar al portal. Redirigiendo al enlace de inicio de sesión...'
            : 'Could not connect to portal. Redirecting to login link...'
        });
        
        // Small delay to show message, then redirect
        setTimeout(() => {
          redirectToStaticLoginLink();
        }, 1500);
        return;
      }
    } catch (error) {
      logger.error('Error creating portal session:', error);
      // Fall back to static login link on error
      redirectToStaticLoginLink();
    } finally {
      setSyncing(false);
    }
  };

  // Handle sync subscription button click
  const handleSyncSubscription = async () => {
    if (!user?.uid) {
      setMessage({ 
        type: 'error', 
        text: i18n.language === 'es' 
          ? 'Usuario no autenticado' 
          : 'User not authenticated' 
      });
      return;
    }

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Debug-Session-Id': '60e2e9',
      },
      body: JSON.stringify({
        sessionId: '60e2e9',
        runId: 'initial',
        hypothesisId: 'H3',
        location: 'src/components/account/SubscriptionStatus.jsx:300',
        message: 'handleSyncSubscription invoked',
        data: {
          hasUser: !!user,
          userId: user?.uid,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion

    setSyncing(true);
    setMessage({ 
      type: 'info', 
      text: i18n.language === 'es' 
        ? 'Actualizando información de suscripción...' 
        : 'Refreshing subscription information...' 
    });

    try {
      const result = await syncSubscription(user.uid);
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Debug-Session-Id': '60e2e9',
        },
        body: JSON.stringify({
          sessionId: '60e2e9',
          runId: 'initial',
          hypothesisId: 'H3',
          location: 'src/components/account/SubscriptionStatus.jsx:320',
          message: 'syncSubscription result',
          data: {
            success: !!result?.success,
            error: result?.error || null,
          },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion
      
      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: i18n.language === 'es' 
            ? 'Suscripción actualizada correctamente.' 
            : 'Subscription updated successfully.' 
        });
        await reloadUser();
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        setMessage({ 
          type: 'error', 
          text: result.error || (i18n.language === 'es' 
            ? 'Error al actualizar la suscripción' 
            : 'Failed to refresh subscription') 
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      logger.error('Error syncing subscription:', error);
      setMessage({ 
        type: 'error', 
        text: i18n.language === 'es' 
          ? 'Error al actualizar la suscripción' 
          : 'Failed to refresh subscription' 
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setSyncing(false);
    }
  };

  const badge = getStatusBadge();

  // If no subscription data
  if (!subscription) {
    return (
      <div>
        <h2 className="text-xl font-semibold text-gray-300 mb-6">
          {i18n.language === 'es' ? 'Estado de Suscripción' : 'Subscription Status'}
        </h2>
        
        <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700 text-center">
          <p className="text-gray-400 mb-4">
            {i18n.language === 'es' 
              ? 'No hay información de suscripción disponible.' 
              : 'No subscription information available.'}
          </p>
          <button
            type="button"
            onClick={() => {
              const plansSection = document.getElementById('subscription-plans');
              if (plansSection) {
                plansSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            className="inline-block px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            {i18n.language === 'es' ? 'Ver Planes de Suscripción' : 'View Subscription Plans'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-300 mb-6">
        {i18n.language === 'es' ? 'Estado de Suscripción' : 'Subscription Status'}
      </h2>

      {message.text && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${
          message.type === 'success' 
            ? 'bg-green-900/50 border border-green-500 text-green-300' 
            : message.type === 'error'
            ? 'bg-red-900/50 border border-red-500 text-red-300'
            : 'bg-blue-900/50 border border-blue-500 text-blue-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Subscription Status Badge */}
      <div className="mb-6">
        <span className={`inline-block px-4 py-2 rounded-lg text-sm font-semibold border ${badge.color} ${badge.border}`}>
          {badge.label}
        </span>
      </div>

      {/* Subscription Details */}
      <div className="space-y-4">
        {/* Plan Information */}
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <h3 className="text-lg font-semibold text-gray-200 mb-4">
            {i18n.language === 'es' ? 'Información del Plan' : 'Plan Information'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-400 mb-1">
                {i18n.language === 'es' ? 'Nombre del Plan' : 'Plan Name'}
              </p>
              <p className="text-gray-200 font-medium">
                {(() => {
                  const subscriptionType = mapPlanIdToSubscriptionType(subscription.planId || 'expired');
                  const planNames = {
                    'free_trial': i18n.language === 'es' ? 'Prueba Gratuita' : 'Free Trial',
                    'daily': i18n.language === 'es' ? 'Suscripción Diaria' : 'Daily Subscription',
                    'monthly_global': i18n.language === 'es' ? 'Mercados Globales Mensual' : 'Monthly Global Markets',
                    'monthly_all': i18n.language === 'es' ? 'Todos los Tableros Mensual' : 'Monthly All Dashboards',
                    'expired': i18n.language === 'es' ? 'Sin Suscripción Activa' : 'No Active Subscription',
                    'cancelled': i18n.language === 'es' ? 'Suscripción Cancelada' : 'Cancelled Subscription'
                  };
                  return planNames[subscriptionType] || getSubscriptionDisplayName(subscriptionType);
                })()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-400 mb-1">
                {i18n.language === 'es' ? 'Estado' : 'Status'}
              </p>
              <p className="text-gray-200 font-medium">
                {(() => {
                  const status = getActualStatus();
                  const statusTranslations = {
                    'active': i18n.language === 'es' ? 'Activo' : 'Active',
                    'trialing': i18n.language === 'es' ? 'En Prueba' : 'Trialing',
                    'canceled': i18n.language === 'es' ? 'Cancelado' : 'Canceled',
                    'cancelled': i18n.language === 'es' ? 'Cancelado' : 'Cancelled',
                    'expired': i18n.language === 'es' ? 'Expirado' : 'Expired',
                    'past_due': i18n.language === 'es' ? 'Pago Pendiente' : 'Past Due',
                    'unpaid': i18n.language === 'es' ? 'Sin Pagar' : 'Unpaid'
                  };
                  return statusTranslations[status] || status;
                })()}
              </p>
            </div>
            {subscription.planId && (
              <div>
                <p className="text-sm text-gray-400 mb-1">
                  {i18n.language === 'es' ? 'ID del Plan' : 'Plan ID'}
                </p>
                <p className="text-gray-200 font-mono text-xs">
                  {subscription.planId}
                </p>
              </div>
            )}
            {subscription.planId === 'free_trial' && subscription.trialEndsAt && (
              <div>
                <p className="text-sm text-gray-400 mb-1">
                  {i18n.language === 'es' ? 'Trial Finaliza' : 'Trial Ends'}
                </p>
                <p className="text-gray-200 font-medium">
                  {formatDate(subscription.trialEndsAt)}
                </p>
              </div>
            )}
            {subscription.amount && (
              <div>
                <p className="text-sm text-gray-400 mb-1">
                  {i18n.language === 'es' ? 'Precio' : 'Price'}
                </p>
                <p className="text-gray-200 font-medium">
                  {formatCurrency(subscription.amount, subscription.currency)}
                  {subscription.planId === 'monthly_global_markets' || subscription.planId === 'monthly_all_dashboards' 
                    ? ` / ${i18n.language === 'es' ? 'mes' : 'month'}`
                    : ''}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Period Information */}
        {(subscription.currentPeriodStart || subscription.currentPeriodEnd) && (
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-gray-200 mb-4">
              {i18n.language === 'es' ? 'Período Actual' : 'Current Period'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {subscription.currentPeriodStart && (
                <div>
                  <p className="text-sm text-gray-400 mb-1">
                    {i18n.language === 'es' ? 'Inicio del Período' : 'Period Start'}
                  </p>
                  <p className="text-gray-200">
                    {formatDate(subscription.currentPeriodStart)}
                  </p>
                </div>
              )}
              {subscription.currentPeriodEnd && (
                <div>
                  <p className="text-sm text-gray-400 mb-1">
                    {i18n.language === 'es' ? 'Fin del Período' : 'Period End'}
                  </p>
                  <p className="text-gray-200">
                    {formatDate(subscription.currentPeriodEnd)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment & Billing Information */}
        {(subscription.lastPaymentDate || subscription.nextBillingDate) && (
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-200">
                {i18n.language === 'es' ? 'Pago y Facturación' : 'Payment & Billing'}
              </h3>
              <p className="text-xs text-gray-400 text-right ml-4">
                {i18n.language === 'es' 
                  ? 'Ve a Gestión de Suscripción para ver el historial de facturas'
                  : 'Go to Subscription Management to see invoice history'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {subscription.lastPaymentDate && (
                <div>
                  <p className="text-sm text-gray-400 mb-1">
                    {i18n.language === 'es' ? 'Último Pago' : 'Last Payment'}
                  </p>
                  <p className="text-gray-200">
                    {formatDate(subscription.lastPaymentDate)}
                  </p>
                </div>
              )}
              {subscription.nextBillingDate && (() => {
                // Don't show next billing date if subscription is cancelled
                const actualStatus = getActualStatus();
                const isCancelled = actualStatus === 'canceled' || actualStatus === 'cancelled' || 
                                   subscription.cancelAtPeriodEnd === true;
                
                if (isCancelled) {
                  return null;
                }
                
                return (
                  <div>
                    <p className="text-sm text-gray-400 mb-1">
                      {i18n.language === 'es' ? 'Próxima Facturación' : 'Next Billing Date'}
                    </p>
                    <p className="text-gray-200">
                      {formatDate(subscription.nextBillingDate)}
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Cancellation Information */}
        {subscription.cancelAtPeriodEnd && (
          <div className="bg-yellow-900/20 rounded-lg p-4 border border-yellow-600/50">
            <h3 className="text-lg font-semibold text-yellow-300 mb-2">
              {i18n.language === 'es' ? 'Aviso de Cancelación' : 'Cancellation Notice'}
            </h3>
            <p className="text-yellow-200 text-sm">
              {i18n.language === 'es' 
                ? 'Tu suscripción está configurada para cancelarse al final del período de facturación actual.'
                : 'Your subscription is set to cancel at the end of the current billing period.'}
              {subscription.currentPeriodEnd && (
                <> {i18n.language === 'es' 
                  ? `Tendrás acceso hasta ${formatDate(subscription.currentPeriodEnd)}.`
                  : `You will have access until ${formatDate(subscription.currentPeriodEnd)}.`}
                </>
              )}
            </p>
          </div>
        )}

        {/* Stripe Information (for support) */}
        {(subscription.stripeCustomerId || subscription.stripeSubscriptionId) && (
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-gray-200 mb-4">
              {i18n.language === 'es' ? 'Detalles de la Cuenta' : 'Account Details'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {subscription.stripeCustomerId && (
                <div>
                  <p className="text-sm text-gray-400 mb-1">
                    {i18n.language === 'es' ? 'ID de Cliente' : 'Customer ID'}
                  </p>
                  <p className="text-gray-200 font-mono text-xs break-all">
                    {subscription.stripeCustomerId}
                  </p>
                </div>
              )}
              {subscription.stripeSubscriptionId && (
                <div>
                  <p className="text-sm text-gray-400 mb-1">
                    {i18n.language === 'es' ? 'ID de Suscripción' : 'Subscription ID'}
                  </p>
                  <p className="text-gray-200 font-mono text-xs break-all">
                    {subscription.stripeSubscriptionId}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {subscription.stripeCustomerId && (
          <div className="flex flex-wrap items-center gap-3 pt-4">
            <button
              type="button"
              onClick={handleSyncSubscription}
              disabled={syncing}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {syncing 
                ? (i18n.language === 'es' ? 'Actualizando...' : 'Refreshing...')
                : (i18n.language === 'es' ? 'Actualizar' : 'Refresh')
              }
            </button>
            <p className="text-sm text-gray-400">
              {i18n.language === 'es' 
                ? 'Si realizaste un cambio en tu suscripción y no se muestra, intenta actualizar'
                : 'If you made a change in your subscription and it\'s not showing, try refreshing'}
            </p>
          </div>
        )}
      </div>

      {/* Subscription Management or Subscribe Button */}
      <div className="mt-6 pt-6 border-t border-gray-700">
        {(() => {
          // Check if user has an active subscription
          const actualStatus = getActualStatus();
          const hasActiveSubscription = actualStatus === 'active' || actualStatus === 'trialing' || actualStatus === 'cancelled';
          
          if (hasActiveSubscription && subscription?.stripeCustomerId) {
            // Show Subscription Management button for active subscriptions
            return (
              <>
                <button
                  type="button"
                  onClick={handleManageSubscription}
                  className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  {i18n.language === 'es' ? 'Gestión de Suscripción' : 'Subscription Management'}
                </button>
                <p className="mt-2 text-xs text-gray-500 text-center">
                  {i18n.language === 'es' 
                    ? 'Serás redirigido al portal de Stripe para gestionar tu suscripción' 
                    : 'You will be redirected to Stripe portal to manage your subscription'}
                </p>
              </>
            );
          } else {
            // Show Subscribe button when no active subscription
            const handleScrollToPlans = () => {
              const plansSection = document.getElementById('subscription-plans');
              if (plansSection) {
                plansSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            };

            return (
              <>
                <button
                  type="button"
                  onClick={handleScrollToPlans}
                  className="block w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-center"
                >
                  {i18n.language === 'es' 
                    ? 'Suscríbete a uno de nuestros planes' 
                    : 'Subscribe to one of our plans'}
                </button>
                <p className="mt-2 text-xs text-gray-500 text-center">
                  {i18n.language === 'es' 
                    ? 'Elige un plan para comenzar' 
                    : 'Choose a plan to get started'}
                </p>
              </>
            );
          }
        })()}
      </div>
    </div>
  );
}

